/**
 * Cable Junction Service Adapter
 *
 * Adapter for the standardized entity hook pattern.
 * Wraps cableJunctionService for use with useEntity hook.
 */

import {
  getCableJunctions,
  createCableJunction,
  updateCableJunction,
  deleteCableJunction,
} from '@/services/cableJunctionService';
import type { CableJunction, UpdateCableJunction } from '@/types/cablePaths';

export const cableJunctionServiceAdapter = {
  async getByFloorPlan(floorPlanId: string): Promise<CableJunction[]> {
    return getCableJunctions(floorPlanId);
  },

  async create(item: Partial<CableJunction>): Promise<CableJunction> {
    // Ensure required fields
    if (!item.floor_plan_id) {
      throw new Error('floor_plan_id is required');
    }
    if (typeof item.x !== 'number' || typeof item.y !== 'number') {
      throw new Error('x and y coordinates are required');
    }

    return createCableJunction({
      floor_plan_id: item.floor_plan_id,
      x: item.x,
      y: item.y,
      name: item.name, // Optional - will auto-generate if not provided
      product_id: item.product_id ?? null,
      notes: item.notes ?? null,
      created_by_clerk_id: item.created_by_clerk_id ?? null,
      scale: item.scale ?? 1.0,
    });
  },

  async update(id: string, updates: Partial<CableJunction>): Promise<CableJunction> {
    // Filter out non-updatable fields
    const { id: _, floor_plan_id: __, created_at: ___, ...updateData } = updates;
    return updateCableJunction(id, updateData as UpdateCableJunction);
  },

  async delete(id: string): Promise<boolean> {
    return deleteCableJunction(id);
  },
};
